/**
 * Dashboard.jsx — Products Intelligence page.
 *
 * Access guard: redirects to /company if products_page_enabled !== true.
 * The system admin controls this flag per tenant via Admin → Features tab.
 * Default is false — new tenants cannot see this page until enabled.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useProduct } from "./hooks/products/useProduct";
import { useFeatureFlags } from "./hooks/useFeatureFlags";
import Navigation from "./components/layout/Navigation";
import HeaderComponent from "./components/HeaderComponent";
import StatsOverviewComponent from "./components/StatsOverviewComponent";
import ControlsSectionComponent from "./components/ControlsSectionComponent";
import ProductDisplayComponent from "./components/ProductDisplayComponent";
import TableViewComponent from "./components/TableViewComponent";
import EmptyStateComponent from "./components/EmptyStateComponent";
import FooterComponent from "./components/FooterComponent";
import applyRules from "./utils/applyRules";

function Dashboard() {
  const navigate = useNavigate();
  const { canViewProducts, features } = useFeatureFlags();

  // ── Access guard ─────────────────────────────────────────
  // Wait until features are loaded before checking, to avoid
  // a false redirect before the tenant flags arrive.
  useEffect(() => {
    const featuresLoaded = features && Object.keys(features).length > 0;
    if (featuresLoaded && !canViewProducts()) {
      navigate("/company", { replace: true });
    }
  }, [features]);

  const {
    darkMode,
    viewMode,
    setViewMode,
    filteredProducts,
    getStatusIcon,
    getStatusColor,
    getConfidenceColor,
    getProgressBarColor,
    getSalesIndicator,
    getStockIndicator,
    getPriceIndicator,
    containerVariants,
    itemVariants,
    stats,
    bgGradient,
    textColor,
    secondaryTextColor,
    cardBg,
    cardBorder,
    inputBg,
    inputBorder,
    buttonBg,
    buttonHoverBg,
    footerBorder,
    tableHeaderBg,
    tableRowBg,
    tableBorder,
    selectedStatus,
    setSelectedStatus,
    searchQuery,
    setSearchQuery,
  } = useProduct(applyRules);

  const safeFilteredProducts = filteredProducts || [];
  const hasProducts = safeFilteredProducts.length > 0;

  // Don't render page content if not permitted
  const featuresLoaded = features && Object.keys(features).length > 0;
  if (featuresLoaded && !canViewProducts()) return null;

  return (
    <div className={`min-h-screen ${bgGradient} ${textColor} transition-colors duration-500`}>
      <Navigation />

      <div className="p-4 md:p-8">
        <HeaderComponent
          darkMode={darkMode}
          viewMode={viewMode}
          setViewMode={setViewMode}
          secondaryTextColor={secondaryTextColor}
        />

        <StatsOverviewComponent
          stats={stats}
          darkMode={darkMode}
          secondaryTextColor={secondaryTextColor}
          cardBg={cardBg}
          cardBorder={cardBorder}
          containerVariants={containerVariants}
          itemVariants={itemVariants}
        />

        <ControlsSectionComponent
          darkMode={darkMode}
          setSelectedStatus={setSelectedStatus}
          selectedStatus={selectedStatus}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cardBorder={cardBorder}
          buttonBg={buttonBg}
          buttonHoverBg={buttonHoverBg}
          inputBg={inputBg}
          inputBorder={inputBorder}
        />

        {viewMode === "cards" ? (
          <ProductDisplayComponent
            darkMode={darkMode}
            filteredProducts={safeFilteredProducts}
            getStatusIcon={getStatusIcon}
            getStatusColor={getStatusColor}
            getConfidenceColor={getConfidenceColor}
            getProgressBarColor={getProgressBarColor}
            getSalesIndicator={getSalesIndicator}
            getStockIndicator={getStockIndicator}
            getPriceIndicator={getPriceIndicator}
            containerVariants={containerVariants}
            itemVariants={itemVariants}
            secondaryTextColor={secondaryTextColor}
            cardBorder={cardBorder}
          />
        ) : (
          <TableViewComponent
            darkMode={darkMode}
            filteredProducts={safeFilteredProducts}
            getStatusIcon={getStatusIcon}
            getStatusColor={getStatusColor}
            getConfidenceColor={getConfidenceColor}
            getProgressBarColor={getProgressBarColor}
            getSalesIndicator={getSalesIndicator}
            getStockIndicator={getStockIndicator}
            getPriceIndicator={getPriceIndicator}
            cardBorder={cardBorder}
            tableHeaderBg={tableHeaderBg}
            tableRowBg={tableRowBg}
            tableBorder={tableBorder}
          />
        )}

        {!hasProducts && (
          <EmptyStateComponent
            darkMode={darkMode}
            cardBorder={cardBorder}
            secondaryTextColor={secondaryTextColor}
          />
        )}

        <FooterComponent darkMode={darkMode} footerBorder={footerBorder} />
      </div>
    </div>
  );
}

export default Dashboard;